import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { PurchaseTypeEnum } from "@enums/purchaseType.enum";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { PurchaseLogLineItem } from "@interfaces/purchaseLogLineItem.interface";
import { MarkOutsideProcessContractMovementsAsSentDto } from "@interfaces/dto/markOutsideProcessContractMovementsAsSentDto.interface";
import { MarkOutsideProcessContractMovementAsReceivedDto } from "@interfaces/dto/markOutsideProcessContractMovementAsReceivedDto.interface";
import { CreateOutsideProcessContractMovementForWorkOrderStageDto } from "@interfaces/dto/createOutsideProcessContractMovementForWorkOrderStageDto.interface";
import { WorkOrder } from "@interfaces/workOrder.interface";
import { AppSettingsService } from "@services/appSettings.service";
import { AuthService } from "@services/auth.service";
import { OutsideProcessContractMovementService } from "@services/outsideProcessContractMovement.service";
import { PurchaseLogService } from "@services/purchaseLog.service";
import { SiteService } from "@services/site.service";
import { WorkOrderService } from "@services/workOrder.service";
import { WorkOrderStageService } from "@services/workOrderStage.service";
import { ToastService } from "angular-toastify";
import { forkJoin } from "rxjs";
import { Guid } from "guid-typescript";

// ZXing (same pattern as MoveStock)
import { BarcodeFormat } from "@zxing/library";
import { ZXingScannerComponent } from "@zxing/ngx-scanner";
import { OutsideProcessContractService } from "@services/outsideProcessContract.service";
import { OutsideProcessContract } from "@interfaces/models/outsideProcessContract.interface";

@Component({
	encapsulation: ViewEncapsulation.None,
	moduleId: module.id,
	selector: "outsideProcess",
	styleUrls: ["outsideProcess.css"],
	templateUrl: "outsideProcess.html"
})
export class OutsideProcessComponent implements OnInit, OnDestroy {
	readonly SWIPE_ACTION_WIDTH = 72;
	readonly SWIPE_THRESHOLD = 36;
	allowedFormats = BarcodeFormat.QR_CODE;
	allowedRouterStages: number[] = [];
	availableDevices: MediaDeviceInfo[] = [];
	dateTime = new Date();
	direction = 0;
	editQty: number | null = null;
	editStillage: string | null = null;
	firstLoad = true;
	hasCameras = false;
	hasPermission = false;
	isLoading = false;
	manualWorkOrderId: number | null = null;
	readonly maxPinLength = 8;
	readonly minPinLength = 4;
	mode = 0;
	movementToDelete: any = null;
	movementToDispatchSingle: any = null;
	movementToEdit: any = null;
	pinContext: "scan" | "manual" | "delete" | "edit" | "dispatch-single" | "receive" = "scan";
	pinError = "";
	pinInput = "";
	pinSlots = Array(8).fill(0);
	purchaseLogs: any[] = [];
	purchaseTypes: any[] = [];
	qtyReceived: number | null = null;
	qtyToSend: number | null = null;
	receivableMovements: any[] = [];
	result = "";
	scannedWorkOrder: WorkOrder | null = null;
	scanner: ZXingScannerComponent = new ZXingScannerComponent();
	scannerCollapsed = window.matchMedia("(orientation: portrait), (max-width: 1100px)").matches;
	scanning = false;
	searchTerm = "";
	selectedDevice: any;
	selectedReceivableMovement: any = null;
	selectedStageId: number | null = null;
	sendableMovements: any[] = [];
	showDeleteConfirmModal = false;
	showDispatchConfirmModal = false;
	showDispatchSingleConfirmModal = false;
	showEditModal = false;
	showPinModal = false;
	showPopup = false;
	showQtyModal = false;
	showReceiveModal = false;
	showWoQtyModal = false;
	site = "";
	siteId = 0;
	stillageToSend: string | null = null;
	swipeActiveId: any = null;
	swipeCurrentOffset = 0;
	swipeDragBase = 0;
	swipeIsHorizontal: boolean | null = null;
	swipeRightActiveId: any = null;
	swipeStartX = 0;
	swipeStartY = 0;
	swipeWasDrag = false;
	swipingId: any = null;
	userCode = 0;

	constructor(
		private route: ActivatedRoute,
		private toastService: ToastService,
		private router: Router,
		private appSettingsService: AppSettingsService,
		private siteService: SiteService,
		private plogService: PurchaseLogService,
		private outsideProcessContractMovementService: OutsideProcessContractMovementService,
		private outsideProcessContractService: OutsideProcessContractService,
		private workOrderService: WorkOrderService,
		private workOrderStageService: WorkOrderStageService,
		private authService: AuthService
	) {
		this.purchaseTypes = GetPropertiesFromEnum(PurchaseTypeEnum);

		this.route.params.subscribe(async (params) => {
			this.siteId = Number.parseInt(params.id);
			this.mode = Number.parseInt(params.mode);
			this.renderMode(Number.parseInt(params.mode));
		});

		this.getSendableMovements();
		this.getReceivableMovements();
	}

	private boundMouseMove = (e: MouseEvent) => this.onMouseSwipeMove(e);
	private boundMouseUp = () => this.onMouseSwipeEnd();

	// Fired by (camerasFound)
	cameraFound(e: any) {
		console.log("[OutsideProcess] camerasFound:", e);

		this.availableDevices = e || [];
		this.hasCameras = this.availableDevices.length > 0;
		this.hasPermission = true;

		// Default select first
		this.selectedDevice = this.availableDevices[0];

		// Prefer back/rear/environment (same regex approach)
		for (const device of this.availableDevices) {
			if (/back|rear|environment/gi.test(device.label)) {
				this.selectedDevice = device;
				break;
			}
		}

		console.log("[OutsideProcess] selectedDevice:", this.selectedDevice);
	}

	// Fired by (camerasNotFound)
	camerasNotFound() {
		console.log("[OutsideProcess] camerasNotFound");
		this.hasCameras = false;
	}

	canSubmit(): boolean {
		return this.pinInput.length >= this.minPinLength;
	}

	clearPin(): void {
		this.pinInput = "";
		this.pinError = "";
	}

	closeEditModal(): void {
		this.showEditModal = false;
		this.movementToEdit = null;
		this.editQty = null;
		this.editStillage = null;
	}

	closeQtyModal() {
		this.showQtyModal = false;
		this.scannedWorkOrder = null;
		this.selectedStageId = null;
		this.allowedRouterStages = [];
		this.stillageToSend = null;
	}

	closeReceiveModal() {
		this.showReceiveModal = false;
		this.selectedReceivableMovement = null;
		this.qtyReceived = null;
	}

	closeWoQtyModal() {
		this.showWoQtyModal = false;
		this.manualWorkOrderId = null;
	}

	confirmDelete() {
		this.showDeleteConfirmModal = false;
		this.pinInput = "";
		this.pinError = "";
		this.pinContext = "delete";
		this.showPinModal = true;
	}

	confirmDispatch() {
		this.showDispatchConfirmModal = false;
		this.dispatchQueue();
	}

	confirmDispatchSingle(): void {
		this.showDispatchSingleConfirmModal = false;
		this.pinInput = "";
		this.pinError = "";
		this.pinContext = "dispatch-single";
		this.showPinModal = true;
	}

	deleteMovement() {
		if (!this.movementToDelete) return;
		this.outsideProcessContractMovementService.deleteSingleById(this.movementToDelete.id).subscribe(
			() => {
				this.toastService.success("Movement Deleted!");
				this.isLoading = false;
				this.movementToDelete = null;
				this.getSendableMovements();
			},
			(err) => {
				console.log(err);
				this.isLoading = false;
				this.toastService.error("Something Went Wrong!");
			}
		);
	}

	dispatchQueue() {
		if (this.sendableMovements.length === 0) return;

		const dto = new MarkOutsideProcessContractMovementsAsSentDto({
			dateSent: new Date(),
			outsideProcessContractMovementIds: this.sendableMovements.map((m) => m.id),
			siteId: this.siteId
		});

		this.isLoading = true;
		this.outsideProcessContractMovementService.app_markOutsideProcessContractMovementsAsSent(dto).subscribe(
			() => {
				this.toastService.success("Dispatch Queue Sent!");
				this.getSendableMovements();
			},
			(err) => {
				console.log(err);
				this.isLoading = false;
				this.toastService.error("Something Went Wrong!");
			}
		);
	}

	dispatchSingleMovement(): void {
		if (!this.movementToDispatchSingle) return;
		const dto = new MarkOutsideProcessContractMovementsAsSentDto({
			dateSent: new Date(),
			outsideProcessContractMovementIds: [this.movementToDispatchSingle.id],
			siteId: this.siteId
		});
		this.outsideProcessContractMovementService.app_markOutsideProcessContractMovementsAsSent(dto).subscribe(
			() => {
				this.toastService.success("Movement Dispatched!");
				this.isLoading = false;
				this.movementToDispatchSingle = null;
				this.getSendableMovements();
			},
			(err) => {
				console.log(err);
				this.isLoading = false;
				this.toastService.error("Something Went Wrong!");
			}
		);
	}

	enterManual() {
		if (this.mode == 1) {
			const input = window.prompt("Enter PO Number", "0");
			this.router.navigate([`admin/webapps/purchaseDetail/${input}`]);
		}
	}

	get filteredReceivableMovements(): any[] {
		if (!this.searchTerm || this.searchTerm.trim() === "") {
			return this.receivableMovements;
		}
		const term = this.searchTerm.trim().toLowerCase();
		return this.receivableMovements.filter((m: any) => {
			const partNumber = m.outsideProcessContract?.part?.partNumber?.toLowerCase() || "";
			const workOrderId = m.workOrderStage?.workOrderId?.toString() || "";
			return partNumber.includes(term) || workOrderId.includes(term);
		});
	}

	getCardTransform(movement: any): string {
		if (this.swipingId === movement.id) {
			return `translateX(${this.swipeCurrentOffset}px)`;
		}
		if (this.swipeActiveId === movement.id) return `translateX(-${this.SWIPE_ACTION_WIDTH}px)`;
		if (this.swipeRightActiveId === movement.id) return `translateX(${this.SWIPE_ACTION_WIDTH}px)`;
		return "translateX(0)";
	}

	getPlogs() {
		this.firstLoad = false;
		this.plogService.app_GetDue().subscribe(
			(res: any) => {
				this.purchaseLogs = res.response;
			},
			(err) => {
				console.log(err);
				this.toastService.error("Something Went Wrong!");
			}
		);
	}

	getReceivableMovements() {
		this.isLoading = true;
		this.outsideProcessContractMovementService.app_GetReceivable().subscribe(
			(res: any) => {
				this.receivableMovements = res.response;
				this.isLoading = false;
			},
			(err) => {
				console.log(err);
				this.isLoading = false;
				this.toastService.error("Something Went Wrong!");
			}
		);
	}

	getSendableMovements() {
		this.isLoading = true;
		this.outsideProcessContractMovementService.app_GetSendable().subscribe(
			(res: any) => {
				this.sendableMovements = res.response;
				this.isLoading = false;
			},
			(err) => {
				console.log(err);
				this.isLoading = false;
				this.toastService.error("Something Went Wrong!");
			}
		);
	}

	goBack() {
		if (this.direction == 0) {
			// Goods In
			this.router.navigate([`admin/webapps/goods/${this.siteId}/in`]);
		} else {
			// Goods Out
			this.router.navigate([`admin/webapps/goods/${this.siteId}/out`]);
		}
	}

	goPurchaseDetail(id: number) {
		this.router.navigate([`admin/webapps/purchaseDetail/${id}`]);
	}

	ngOnDestroy(): void {
		document.removeEventListener("mousemove", this.boundMouseMove);
		document.removeEventListener("mouseup", this.boundMouseUp);
	}

	ngOnInit(): void {
		document.addEventListener("mousemove", this.boundMouseMove);
		document.addEventListener("mouseup", this.boundMouseUp);
	}

	onBackspace(): void {
		if (this.pinInput.length === 0) return;
		this.pinInput = this.pinInput.slice(0, -1);
		this.pinError = "";
	}

	onCardClick(movement: any): void {
		if (this.swipeWasDrag) {
			this.swipeWasDrag = false;
			return;
		}
		if (this.swipeActiveId === movement.id) {
			this.swipeActiveId = null;
			return;
		}
		if (this.swipeRightActiveId === movement.id) {
			this.swipeRightActiveId = null;
			return;
		}
		this.openEditModal(movement);
	}

	// If you add a device <select>, call this on change
	onDeviceSelectChange() {
		console.log("[OutsideProcess] device changed:", this.selectedDevice?.label ?? this.selectedDevice);
	}

	onDigitPress(digit: string): void {
		if (this.pinInput.length >= this.maxPinLength) return;
		this.pinInput += digit;
		this.pinError = "";
	}

	onMouseSwipeEnd(): void {
		if (!this.swipingId) return;
		if (this.swipeCurrentOffset <= -this.SWIPE_THRESHOLD) {
			this.swipeActiveId = this.swipingId;
			this.swipeRightActiveId = null;
		} else if (this.swipeCurrentOffset >= this.SWIPE_THRESHOLD) {
			this.swipeRightActiveId = this.swipingId;
			this.swipeActiveId = null;
		} else {
			this.swipeActiveId = null;
			this.swipeRightActiveId = null;
		}
		this.swipingId = null;
	}

	onMouseSwipeMove(event: MouseEvent): void {
		if (!this.swipingId) return;
		const dx = event.clientX - this.swipeStartX;
		const dy = event.clientY - this.swipeStartY;
		if (this.swipeIsHorizontal === null && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
			this.swipeIsHorizontal = Math.abs(dx) > Math.abs(dy);
			if (!this.swipeIsHorizontal) { this.swipingId = null; return; }
		}
		if (!this.swipeIsHorizontal) return;
		this.swipeWasDrag = true;
		this.swipeCurrentOffset = Math.max(-this.SWIPE_ACTION_WIDTH, Math.min(this.SWIPE_ACTION_WIDTH, this.swipeDragBase + dx));
	}

	onMouseSwipeStart(event: MouseEvent, movement: any): void {
		if (event.button !== 0) return;
		if (this.swipeActiveId && this.swipeActiveId !== movement.id) this.swipeActiveId = null;
		if (this.swipeRightActiveId && this.swipeRightActiveId !== movement.id) this.swipeRightActiveId = null;
		this.swipingId = movement.id;
		this.swipeStartX = event.clientX;
		this.swipeStartY = event.clientY;
		this.swipeDragBase = this.swipeActiveId === movement.id ? -this.SWIPE_ACTION_WIDTH
			: this.swipeRightActiveId === movement.id ? this.SWIPE_ACTION_WIDTH : 0;
		this.swipeCurrentOffset = this.swipeDragBase;
		this.swipeIsHorizontal = null;
		this.swipeWasDrag = false;
		event.preventDefault();
	}

	onOkPress(): void {
		if (this.canSubmit() && !this.isLoading) this.submitPin();
	}

	onPinCancel(): void {
		this.pinInput = "";
		this.pinError = "";
		this.showPinModal = false;
		if (this.pinContext === "edit" && this.movementToEdit) {
			this.showEditModal = true;
		}
	}

	onPinSuccess(): void {
		if (this.pinContext === "receive") {
			this.isLoading = false;
			this.showReceiveModal = true;
		} else if (this.pinContext === "manual") {
			this.isLoading = false;
			this.openWoQtyModal();
		} else if (this.pinContext === "delete") {
			this.deleteMovement();
		} else if (this.pinContext === "edit") {
			this.saveEditMovement();
		} else if (this.pinContext === "dispatch-single") {
			this.dispatchSingleMovement();
		} else {
			// Scan flow: fetch allowed contracts then open stage/qty modal
			this.outsideProcessContractService.getAllowedForWorkOrder(this.scannedWorkOrder!.id).subscribe(
				(contractRes: any) => {
					this.allowedRouterStages = contractRes.response;
					this.isLoading = false;
					this.openQtyModal();
				},
				(err) => {
					console.log(err);
					this.isLoading = false;
					this.toastService.error("Something Went Wrong!");
				}
			);
		}
	}

	// Fired by (scanSuccess)
	onScanSuccess(result: string) {
		this.result = result;
		console.log("[OutsideProcess] scanSuccess:", result);
		this.scanning = false;
		this.isLoading = true;

		this.workOrderService.app_GetByLookup(Guid.parse(result)).subscribe(
			(res: any) => {
				this.scannedWorkOrder = res.response;
				this.isLoading = false;
				// Auth immediately so we can use getAllowedForWorkOrder
				this.openPinModal();
			},
			(err) => {
				console.log(err);
				this.isLoading = false;
				this.toastService.error("Work order not found. Please try again.");
			}
		);
	}

	onSwipeEnd(event: TouchEvent, movement: any): void {
		if (this.swipingId !== movement.id) return;
		if (this.swipeCurrentOffset <= -this.SWIPE_THRESHOLD) {
			this.swipeActiveId = movement.id;
			this.swipeRightActiveId = null;
		} else if (this.swipeCurrentOffset >= this.SWIPE_THRESHOLD) {
			this.swipeRightActiveId = movement.id;
			this.swipeActiveId = null;
		} else {
			this.swipeActiveId = null;
			this.swipeRightActiveId = null;
		}
		this.swipingId = null;
	}

	onSwipeMove(event: TouchEvent, movement: any): void {
		if (this.swipingId !== movement.id) return;
		const dx = event.touches[0].clientX - this.swipeStartX;
		const dy = event.touches[0].clientY - this.swipeStartY;
		if (this.swipeIsHorizontal === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
			this.swipeIsHorizontal = Math.abs(dx) > Math.abs(dy);
			if (!this.swipeIsHorizontal) { this.swipingId = null; return; }
		}
		if (!this.swipeIsHorizontal) return;
		this.swipeWasDrag = true;
		event.preventDefault();
		this.swipeCurrentOffset = Math.max(-this.SWIPE_ACTION_WIDTH, Math.min(this.SWIPE_ACTION_WIDTH, this.swipeDragBase + dx));
	}

	onSwipeStart(event: TouchEvent, movement: any): void {
		if (this.swipeActiveId && this.swipeActiveId !== movement.id) this.swipeActiveId = null;
		if (this.swipeRightActiveId && this.swipeRightActiveId !== movement.id) this.swipeRightActiveId = null;
		this.swipingId = movement.id;
		this.swipeStartX = event.touches[0].clientX;
		this.swipeStartY = event.touches[0].clientY;
		this.swipeDragBase = this.swipeActiveId === movement.id ? -this.SWIPE_ACTION_WIDTH
			: this.swipeRightActiveId === movement.id ? this.SWIPE_ACTION_WIDTH : 0;
		this.swipeCurrentOffset = this.swipeDragBase;
		this.swipeIsHorizontal = null;
		this.swipeWasDrag = false;
	}

	openDeleteConfirm(movement: any) {
		this.movementToDelete = movement;
		this.swipeActiveId = null;
		this.showDeleteConfirmModal = true;
	}

	openDispatchConfirm() {
		if (this.sendableMovements.length === 0) return;
		this.showDispatchConfirmModal = true;
	}

	openDispatchSingleConfirm(movement: any): void {
		this.movementToDispatchSingle = movement;
		this.swipeRightActiveId = null;
		this.showDispatchSingleConfirmModal = true;
	}

	openEditModal(movement: any): void {
		this.movementToEdit = movement;
		this.editQty = movement.quantitySent;
		this.editStillage = movement.stillageNumber ?? null;
		this.showEditModal = true;
	}

	openManualEntryPin(): void {
		this.pinInput = "";
		this.pinError = "";
		this.pinContext = "manual";
		this.showPinModal = true;
	}

	openPinModal(): void {
		this.pinInput = "";
		this.pinError = "";
		this.pinContext = "scan";
		this.showPinModal = true;
	}

	openQtyModal() {
		this.qtyToSend = null;
		this.selectedStageId = null;
		this.showQtyModal = true;
	}

	openReceiveModal(movement: any) {
		this.selectedReceivableMovement = movement;
		this.qtyReceived = null;
		this.pinInput = "";
		this.pinError = "";
		this.pinContext = "receive";
		this.showPinModal = true;
	}

	openWoQtyModal() {
		this.manualWorkOrderId = null;
		this.showWoQtyModal = true;
	}

	renderMode(mode: number) {
		if (mode < 4) {
			this.direction = 0;
		} else {
			this.direction = 1;
		}

		switch (mode) {
			case 1:
				// In - Purchase
				if (this.firstLoad == true) {
					this.firstLoad = false;
					this.getPlogs();
				}
				return "Purchase";
			case 2:
				// In Outside Process
				return "Outside Process";
			case 3:
				// In - Internal
				return "Internal";
			case 4:
				//  Out - Suppliers
				return "Suppliers";
			case 5:
				// Out - Customer
				return "Customer";
			case 6:
				// Out - Internal
				return "Internal";
			default:
				this.direction = 999;
				return "UNKNOWN";
		}
	}

	renderPlogDueDate(date: string) {
		return new Date(date).toLocaleDateString("en-GB");
	}

	renderPlogLineItems(lineItems: string) {
		const lineItemsConverted: PurchaseLogLineItem[] = JSON.parse(lineItems);
		return lineItemsConverted.length;
	}

	renderPlogType(id: number) {
		return this.purchaseTypes.filter((x) => x.id == id)[0].name;
	}

	renderSite(id: number) {
		switch (id) {
			case 1:
				return "KINGS NORTON";
			case 2:
				return "SELLY OAK";
			case 3:
				return "REDDITCH";
			default:
				return "KINGS NORTON";
		}
	}

	saveEditMovement(): void {
		if (!this.movementToEdit || !this.editQty) return;
		this.outsideProcessContractMovementService.updateSingleById(this.movementToEdit.id, {
			quantitySent: this.editQty,
			stillageNumber: this.editStillage || null
		}).subscribe(
			() => {
				this.toastService.success("Movement Updated!");
				this.isLoading = false;
				this.closeEditModal();
				this.getSendableMovements();
			},
			(err) => {
				console.log(err);
				this.isLoading = false;
				this.toastService.error("Something Went Wrong!");
			}
		);
	}

	submitEditModal(): void {
		if (!this.movementToEdit || !this.editQty) return;
		this.showEditModal = false;
		this.pinInput = "";
		this.pinError = "";
		this.pinContext = "edit";
		this.showPinModal = true;
	}

	submitPin(): void {
		this.isLoading = true;
		this.authService.loginEmployeeCode(this.pinInput).subscribe(
			() => {
				this.pinInput = "";
				this.pinError = "";
				this.showPinModal = false;

				// Fetch the authenticated user's profile to get their profileId
				this.authService.getCurrentUserProfile().subscribe(
					(profileRes: any) => {
						this.userCode = profileRes.response.id;
						this.appSettingsService.set("profileId", this.userCode);
						this.onPinSuccess();
					},
					(err) => {
						console.log(err);
						this.isLoading = false;
						this.toastService.error("Could not retrieve user profile.");
					}
				);
			},
			(err) => {
				console.log(err);
				this.isLoading = false;
				this.pinInput = "";
				this.pinError = "Invalid PIN. Please try again.";
			}
		);
	}

	submitQtyModal() {
		this.isLoading = true;

		// selectedStageId holds the router stage index (e.g. 3 → OP30).
		// The matching WorkOrderStage has stageNumber equal to the router stage index.
		const workOrderStage = this.scannedWorkOrder!.workOrderStages.find(
			s => s.stageNumber === this.selectedStageId!
		);

		if (!workOrderStage) {
			this.toastService.error("Could not find a matching work order stage. Please try again.");
			this.isLoading = false;
			return;
		}

		this.outsideProcessContractService.getActiveByPartAndStage(
			this.scannedWorkOrder!.partId,
			this.selectedStageId!
		).subscribe(
			(res: any) => {
				const contract: OutsideProcessContract = res.response;

				const dto = new CreateOutsideProcessContractMovementForWorkOrderStageDto({
					outsideProcessContractId: contract.id,
					quantitySent: this.qtyToSend!,
					stillageNumber: this.stillageToSend ?? undefined,
					supplierId: contract.supplierId,
					workOrderStageId: workOrderStage.id
				});

				this.outsideProcessContractMovementService.createSingle(dto).subscribe(
					() => {
						this.toastService.success("Movement Created Successfully!");
						this.isLoading = false;
						this.closeQtyModal();
						this.getSendableMovements();
					},
					(err) => {
						console.log(err);
						this.isLoading = false;
						this.toastService.error("Something Went Wrong!");
					}
				);
			},
			(err) => {
				console.log(err);
				this.isLoading = false;
				this.toastService.error("Something Went Wrong!");
			}
		);
	}

	submitReceiveModal() {
		if (!this.selectedReceivableMovement || !this.qtyReceived) return;

		const dto = new MarkOutsideProcessContractMovementAsReceivedDto({
			dateReceived: new Date(),
			outsideProcessContractMovementId: this.selectedReceivableMovement.id,
			quantityReceived: this.qtyReceived
		});

		this.isLoading = true;
		this.outsideProcessContractMovementService.app_MarkOutsideProcessContractMovementAsReceived(dto).subscribe(
			() => {
				this.toastService.success("Movement Received Successfully!");
				this.isLoading = false;
				this.closeReceiveModal();
				this.getReceivableMovements();
			},
			(err) => {
				console.log(err);
				this.isLoading = false;
				this.toastService.error("Something Went Wrong!");
			}
		);
	}

	submitWoQtyModal() {
		if (!this.manualWorkOrderId) return;
		this.isLoading = true;
		let woGuid: Guid = Guid.parse(Guid.EMPTY);
		this.workOrderService.getSingleById(this.manualWorkOrderId).subscribe(
			(res: any) => {
				woGuid = Guid.parse(res.response.lookupId);
			},
			(err) => {
				console.log(err);
				this.isLoading = false;
				this.toastService.error("Something Went Wrong!");
			},
			() => {
				forkJoin({
					stages: this.workOrderStageService.getForWorkOrder(this.manualWorkOrderId!),
					workOrder: this.workOrderService.app_GetByLookup(woGuid)
				}).subscribe(
					(results: any) => {
						const wo = results.workOrder.response;
						wo.workOrderStages = results.stages.response;
						this.scannedWorkOrder = wo;
						this.closeWoQtyModal();

						// Fetch allowed contracts for this work order before opening the stage modal
						this.outsideProcessContractService.getAllowedForWorkOrder(wo.id).subscribe(
							(contractRes: any) => {
								this.allowedRouterStages = contractRes.response;
								this.isLoading = false;
								this.openQtyModal();
							},
							(err) => {
								console.log(err);
								this.isLoading = false;
								this.toastService.error("Something Went Wrong!");
							}
						);
					},
					(err) => {
						console.log(err);
						this.isLoading = false;
						this.toastService.error("Work order not found. Please check the ID and try again.");
					}
				);
			}
		);



	}

	toggleScanning() {
		this.scanning = !this.scanning;
	}
}
